import { faroCoreApiClient } from "@api/faro-core-api-client";
import { SNAPSHOT_TASK_TYPES } from "@api/progress-api/progress-api-constants";
import { countTasksInProgress } from "@api/progress-api/progress-api-utils";
import { createMutationSetElementMetaData } from "@faro-lotv/service-wires";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchGroupDetails } from "@store/groups/groups-slice-thunk";
import { updateMemberRoleInProjectBase, isForbiddenProjectSettingsError } from "@store/projects/projects-slice-utils";
import { isProjectDemo, projectStateToApiArchivingState } from "@utils/project-utils";

/** Result of fetching project snapshots */

/** Result of fetching snapshot members */

// TODO: No need to pass companyId as createAsyncThunk can access the store: https://faro01.atlassian.net/browse/HBD-253
/**
 * Fetches projects queried by the passed params.
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchProjects = createAsyncThunk("projects/fetchProjects", async ({
  companyId,
  projectArchivingState,
  next,
  search,
  groupId,
  sortBy
}) => {
  return await faroCoreApiClient.projects.getProjects({
    companyId,
    archivingStates: projectStateToApiArchivingState(projectArchivingState),
    start: next,
    search,
    groupId,
    sortBy
  });
});

/**
 * Fetch project detail by providing projectId from the backend
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchProjectDetails = createAsyncThunk("projects/fetchProjectDetails", async ({
  companyId,
  projectId
}) => {
  return await faroCoreApiClient.projects.getProjectDetails({
    companyId,
    projectId
  });
});

/**
 * Fetches the project again after the current user changed their own access to it, to learn what
 * they may still do in it. Unlike fetchProjectDetails, it does not select the project, and its
 * failure is not reported to the user: it means they can no longer access the project, which is
 * then forgotten.
 */
export const refreshProjectAccess = createAsyncThunk("projects/refreshProjectAccess", async ({
  companyId,
  projectId
}) => {
  return await faroCoreApiClient.projects.getProjectDetails({
    companyId,
    projectId
  });
});

/**
 * Update project details
 * @throws {Error} if no companyId or projectId exist to update the project details
 * or if the project is a demo project
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateProjectDetails = createAsyncThunk("projects/updateProjectDetails", async ({
  projectId,
  payload
}, {
  getState,
  dispatch
}) => {
  const {
    projects,
    projects: {
      selectedProject
    },
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();

  /**
   * The ID of the project that is going to be updated.
   * If projectId is not provided, the selectedProjectId from store is selected
   */
  const updatingProjectId = projectId || selectedProject.id;
  if (!selectedSdbCompanyId || !updatingProjectId) {
    throw new Error("No companyId or projectId was given to updateProjectDetails");
  }
  if (selectedProject.id && isProjectDemo(projects.entities[selectedProject.id])) {
    throw new Error("Demo project can't be edited");
  }
  const updatedProject = await faroCoreApiClient.projects.updateProjectDetails({
    companyId: selectedSdbCompanyId,
    projectId: updatingProjectId,
    payload
  });

  // Re-fetch the group so the "Active projects" KPI stays accurate.
  // Only when the user is viewing that group (avoids setting selectedGroupId as a side effect).
  // Skip during bulk actions — those refetch the group once at the end.
  const groupId = updatedProject.group?.id;
  const {
    selectedGroupId
  } = getState().groups;
  const {
    bulkActionName
  } = getState().table;
  if ("archivingState" in payload && groupId && selectedGroupId === groupId && bulkActionName === null) {
    void dispatch(fetchGroupDetails({
      companyId: selectedSdbCompanyId,
      groupId
    }));
  }
  return updatedProject;
});

/** Props to hand a project over to another project manager */

/**
 * Hands a project over to another project manager, keeping it in its group. Unlike
 * updateProjectDetails, this works for demo projects too, whose manager has to be replaced when
 * the current one loses their Sphere XG seat.
 *
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const changeProjectManager = createAsyncThunk("projects/changeProjectManager", async ({
  projectId,
  projectManagerIdentity,
  groupId
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to change the project manager");
  }
  return await faroCoreApiClient.projects.updateProjectDetails({
    companyId: selectedSdbCompanyId,
    projectId,
    payload: {
      projectManagerIdentity,
      groupId
    }
  });
});

/**
 * Remove a member from the project using the backend,
 * and then removes it from project in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const removeMemberFromProject = createAsyncThunk("projects/removeMemberFromProject", async ({
  companyId,
  projectId,
  member
}) => {
  await faroCoreApiClient.projects.removeMemberFromProject({
    companyId,
    projectId,
    userIdentity: member.identity
  });
  return {
    projectId,
    member
  };
});

/**
 * Send request to backend to edit project feature,
 * and then update it in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const editProjectFeature = createAsyncThunk("projects/editProjectFeature", async ({
  companyId,
  selectedProject,
  payload
}) => {
  const newFeatures = await faroCoreApiClient.projects.setProjectFeatures({
    companyId,
    projectId: selectedProject.id,
    payload
  });
  return {
    newFeatures,
    selectedProject
  };
});

/**
 * Fetch all snapshots of a project from the backend
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchProjectSnapshots = createAsyncThunk("projects/fetchProjectSnapshots", async ({
  projectId
}) => {
  const response = await faroCoreApiClient.projects.getAllSnapshotsOfProject({
    projectId,
    limit: 500
  });
  return {
    projectId,
    snapshots: response.data
  };
});

/**
 * Fetches from the progress API the number of snapshots that are currently being
 * created for the selected project, by counting the snapshot creation tasks that
 * have not finished yet.
 * @throws {Error} if the Progress API client request fails
 */
export const fetchSnapshotsBeingCreatedCount = createAsyncThunk("projects/fetchSnapshotsBeingCreatedCount", async ({
  progressApiClient
}) => {
  const {
    data
  } = await progressApiClient.requestProgress({
    before: null,
    taskType: SNAPSHOT_TASK_TYPES.CreateProjectSnapshotTask
  });
  return countTasksInProgress(data);
});

/**
 * Delete a snapshot from a project
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const deleteProjectSnapshot = createAsyncThunk("projects/deleteProjectSnapshot", async ({
  projectId,
  snapshotId
}) => {
  await faroCoreApiClient.projects.deleteProjectSnapshot({
    projectId,
    snapshotId
  });
  return {
    projectId,
    snapshotId
  };
});

/**
 * Fetch all members of a snapshot from the backend
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchSnapshotMembers = createAsyncThunk("projects/fetchSnapshotMembers", async ({
  snapshotId
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetchSnapshotMembers");
  }
  const response = await faroCoreApiClient.projects.getSnapshotMembers({
    companyId: selectedSdbCompanyId,
    snapshotId
  });
  return {
    members: response.projectMembers
  };
});

/**
 * Invite a member to the snapshot using the backend,
 * and then adds it to selectedSnapshotMembers in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const inviteMemberToSnapshot = createAsyncThunk("projects/inviteMemberToSnapshot", async ({
  companyId,
  snapshotId,
  payload
}) => {
  return await faroCoreApiClient.projects.addMembersToSnapshot({
    companyId,
    snapshotId,
    payload
  });
});

/**
 * Remove a member from the snapshot using the backend,
 * and then removes it from selectedSnapshotMembers in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const removeMemberFromSnapshot = createAsyncThunk("projects/removeMemberFromSnapshot", async ({
  companyId,
  snapshotId,
  member
}) => {
  await faroCoreApiClient.projects.removeMemberFromSnapshot({
    companyId,
    snapshotId,
    userIdentity: member.identity
  });
  return {
    member
  };
});

/**
 * Fetch project context for a project from backend, and then update it in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchProjectContext = createAsyncThunk("projects/fetchProjectContext", async ({
  projectId
}) => {
  return await faroCoreApiClient.projects.getProjectContext({
    projectId
  });
});

/**
 * Fetch the features available to the user in a project from its context, and store them on
 * the project. Used where the project was loaded from an endpoint that doesn't return them,
 * e.g. the projects map. Unlike fetchProjectContext, it doesn't set the selected project context.
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchProjectFeaturesAvailable = createAsyncThunk("projects/fetchProjectFeaturesAvailable", async ({
  projectId
}) => {
  const context = await faroCoreApiClient.projects.getProjectContext({
    projectId
  });
  return context.featuresAvailable;
});

/**
 * Delete a project from the backend
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const deleteProject = createAsyncThunk("projects/deleteProject", async ({
  companyId,
  projectId
}) => {
  await faroCoreApiClient.projects.deleteProject({
    companyId,
    projectId
  });
  return projectId;
});

/**
 * Update a member role in the project using the backend,
 * and then update it in project in the store as well.
 * Intended to use when updating a single member role,
 * since the error will be automatically handled by the error slice.
 */
export const updateMemberRoleInProjectSingle = createAsyncThunk("projects/updateMemberRoleInProjectSingle", updateMemberRoleInProjectBase);

/**
 * Update a member role in the project using the backend,
 * and then update it in project in the store as well.
 * Intended to use when updating a member roles in bulk,
 * since the error won't be automatically handled by the error slice.
 */
export const updateMemberRoleInProjectBulk = createAsyncThunk("projects/updateMemberRoleInProjectBulk", updateMemberRoleInProjectBase);

/**
 * Fetch project settings for a project from the Project API backend.
 * The settings are included in the "metaDataMap" property of root ielement of the project.
 * @throws {ApiResponseError} if the Project API client request fails
 */
export const fetchProjectSettings = createAsyncThunk("projects/fetchProjectSettings", async ({
  projectApiClient
}) => {
  try {
    const rootElement = await projectApiClient.getRootIElement();
    const shouldDisplayMarkupsForViewers = rootElement.metaDataMap?.slideContainerMarkupSettings?.displayForViewers;
    return {
      shouldDisplayMarkupsForViewers
    };
  } catch (error) {
    // 403 is expected when users cannot access project settings; keep UI stable without raising a global error.
    if (isForbiddenProjectSettingsError(error)) {
      return {};
    }
    throw error;
  }
});

/**
 * Updates the project settings for the current project
 * @returns the update project settings
 * @throws {ApiResponseError} if the Project API client request fails
 */
export const updateProjectSettings = createAsyncThunk("projects/updateProjectSettings", async ({
  projectApiClient,
  payload
}) => {
  // Fetch the root IElement of the project. The ID of the root is required to update the settings
  const {
    id
  } = await projectApiClient.getRootIElement();
  const setting = {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by Project Api backend
    displayForViewers: payload.shouldDisplayMarkupsForViewers
  };
  const mutation = createMutationSetElementMetaData(id, [{
    key: "slideContainerMarkupSettings",
    value: setting,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by Project Api backend
    skipIfPresent: false
  }]);

  // Update "displayForViewers" value of the root IElement
  await projectApiClient.applyMutations([mutation]);

  // Fetch the root IElement again to get the updated setting value
  const root = await projectApiClient.getRootIElement();
  const shouldDisplayMarkupsForViewers = root.metaDataMap?.slideContainerMarkupSettings?.displayForViewers;
  return {
    shouldDisplayMarkupsForViewers
  };
});

/**
 * Fetch projects by providing projectIds from the backend
 * @throws {Error} if there is no selected companyId in the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchProjectsByIds = createAsyncThunk("projects/fetchProjectsByIds", async ({
  projectIds
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId or projectId exist to was given to updateProjectDetails");
  }
  const fetchedProjects = await Promise.allSettled([...new Set(projectIds)].map(projectId => faroCoreApiClient.projects.getProjectDetails({
    companyId: selectedSdbCompanyId,
    projectId
  })));

  // Extract only fulfilled values
  const successfulProjects = fetchedProjects.filter(result => result.status === "fulfilled").map(result => result.value);
  return successfulProjects;
});

/**
 * Update snapshot details
 * @throws {Error} if no projectId exist to update the snapshot details
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateSnapshotDetails = createAsyncThunk("projects/updateSnapshotDetails", async ({
  snapshotId,
  payload
}, {
  getState
}) => {
  const {
    projects: {
      selectedProject
    }
  } = getState();
  if (!selectedProject.id) {
    throw new Error("No projectId exist was given to updateSnapshotDetails");
  }
  const updatedSnapshot = await faroCoreApiClient.projects.updateSnapshotDetails({
    projectId: selectedProject.id,
    snapshotId,
    payload
  });
  return updatedSnapshot;
});